import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useQuery } from 'convex/react'
import { PartyPopper, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { usePromoCountdown } from '@/lib/promo'

const dismissKey = (key: string) => `goaltree_promo_dismissed_${key}`

function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(dismissKey(key)) === '1'
  } catch {
    return false
  }
}

// Seasonal sale card (see lib/campaign). Free users only; dismissing hides it
// for the rest of that campaign.
export function PromoBanner({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const { isPro, isLoading } = useIsPro()
  const pricing = useQuery(api.subscriptions.getPricing, isAuthenticated ? {} : 'skip')
  const promo = pricing?.promo?.web ? pricing.promo : null
  const countdown = usePromoCountdown(promo)
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  if (!promo || !countdown.live || isLoading || isPro) return null
  if (dismissed === promo.key || readDismissed(promo.key)) return null

  function dismiss() {
    try {
      localStorage.setItem(dismissKey(promo!.key), '1')
    } catch {}
    setDismissed(promo!.key)
  }

  return (
    <>
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/15 to-primary/[0.04] px-4 py-3',
          className,
        )}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          className="absolute right-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
            <PartyPopper className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-foreground">
              {t('promo.bannerTitle', { pct: promo.pct })}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('promo.bannerBody')}</p>
            <p className="mt-1 text-xs font-semibold tabular-nums text-primary-text">
              {countdown.lastDay ? `${t('promo.lastDay')} · ` : ''}
              {countdown.label}
            </p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-2.5 flex w-full cursor-pointer items-center justify-center rounded-full bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99]"
            >
              {t('promo.bannerCta')}
            </button>
          </div>
        </div>
      </div>
      <UpgradeDialog open={open} onOpenChange={setOpen} reason="new-year" />
    </>
  )
}
